/*
 * The demand-led article cluster.
 *
 * Twenty-six articles in the seven topic areas where Google Search Console shows measured demand for
 * bluebear.ai, weighted by that demand rather than split evenly: audit trail carried roughly 51
 * impressions across 15 phrasings against a single published article, control plane ~27 across 5,
 * security ~12, workspace boundary ~11. Every non-brand query in that export had zero clicks, which
 * in AI features usually means the assistant answered from the page and the reader never clicked -
 * so the target is being the cited source, not click-through.
 *
 * Two articles from the original 28-article plan were deliberately NOT written, because the existing
 * corpus already serves their intent and a thinner duplicate would split traffic with a page that
 * already ranks:
 *
 *   - `do-you-need-an-ai-agent-control-plane` -> deferred to `ai-agent-control-plane-explained`,
 *     whose title is literally "What Is an AI Agent Control Plane - and When Does a Business Need
 *     One?". The decision test it was to carry is that article's second half.
 *   - `human-approval-for-ai-agents` -> deferred to `human-in-the-loop-ai-agent-approvals`. The
 *     "authority, not a button" angle is carried instead inside `ai-audit-trail-requirements`
 *     (link 4, which is about what an approval record must contain to establish anything) and
 *     `ai-agent-evidence-chain-explained`.
 *
 * Audience and editorial rules are stated at the top of each cluster file and enforced by
 * demandLedPosts.test.js.
 */

import { auditTrailPosts } from './auditTrailPosts.js';
import { agentSecurityPosts } from './agentSecurityPosts.js';
import { controlPlanePosts } from './controlPlanePosts.js';
import { workspaceBoundaryPosts } from './workspaceBoundaryPosts.js';
import { agentTelemetryPosts, agentIdentityPosts } from './agentTelemetryIdentityPosts.js';
import { agentCategoryPosts } from './agentCategoryPosts.js';

/**
 * Grouped by demand cluster rather than by file, because the stage-coverage rule is per cluster:
 * a reader who does not yet know this is a category and a reader building a shortlist are different
 * people, and every cluster has to serve both.
 */
export const DEMAND_LED_CLUSTERS = {
  auditTrail: auditTrailPosts,
  security: agentSecurityPosts,
  controlPlane: controlPlanePosts,
  workspaceBoundary: workspaceBoundaryPosts,
  observability: agentTelemetryPosts,
  identity: agentIdentityPosts,
  category: agentCategoryPosts,
};

export const demandLedPosts = Object.values(DEMAND_LED_CLUSTERS).flat();
